import IntelligentContentParser from './IntelligentContentParser';

const STORAGE_PREFIX = 'smartPasteReviewMemory';
const MAX_STORED_DECISIONS = 200;

const normalizeName = (value) => IntelligentContentParser.normalizeParsedEntryName(value);

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (error) {
    return null;
  }
  return null;
};

export const buildReviewDecisionStorageKey = (characterId) => (
  `${STORAGE_PREFIX}:${String(characterId || 'global')}`
);

export const buildReviewDecisionKeys = (item) => {
  if (!item || typeof item !== 'object') {
    return { exactKey: null, nameKey: null };
  }

  const sourceKind = item?.parserMeta?.sourceSection || item?.suggestedType || 'review';
  const exactFingerprint = IntelligentContentParser.buildParsedEntryFingerprint(item, sourceKind);
  const normalizedName = normalizeName(item?.name || '');
  const normalizedType = normalizeName(item?.suggestedType || sourceKind || 'review');

  return {
    exactKey: exactFingerprint ? `exact|${exactFingerprint}` : null,
    nameKey: normalizedName ? `name|${normalizedType}|${normalizedName}` : null
  };
};

const pruneReviewDecisions = (preferences) => {
  const entries = Object.entries(preferences || {});
  if (entries.length <= MAX_STORED_DECISIONS) return preferences || {};

  return entries
    .sort((left, right) => {
      const leftTime = Date.parse(left[1]?.updatedAt || '') || 0;
      const rightTime = Date.parse(right[1]?.updatedAt || '') || 0;
      return rightTime - leftTime;
    })
    .slice(0, MAX_STORED_DECISIONS)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
};

export const getRememberedReviewCategories = (preferences, item) => {
  if (!preferences || typeof preferences !== 'object' || !item || typeof item !== 'object') return null;

  const { exactKey, nameKey } = buildReviewDecisionKeys(item);
  const exactCategories = exactKey ? preferences[exactKey]?.categories || preferences[exactKey] : null;
  if (Array.isArray(exactCategories) && exactCategories.length > 0) {
    return exactCategories;
  }

  const nameCategories = nameKey ? preferences[nameKey]?.categories || preferences[nameKey] : null;
  if (Array.isArray(nameCategories) && nameCategories.length > 0) {
    return nameCategories;
  }

  return null;
};

export const recordReviewDecision = (preferences, { item, categories } = {}) => {
  if (!item || !Array.isArray(categories) || categories.length === 0) {
    return preferences || {};
  }

  const { exactKey, nameKey } = buildReviewDecisionKeys(item);
  const next = { ...(preferences || {}) };
  const payload = {
    categories: Array.from(new Set(categories.filter(Boolean))),
    updatedAt: new Date().toISOString()
  };

  if (exactKey) next[exactKey] = payload;
  if (nameKey) next[nameKey] = payload;

  return pruneReviewDecisions(next);
};

export const recordReviewDecisions = (preferences, decisions = []) => (
  (Array.isArray(decisions) ? decisions : []).reduce(
    (acc, decision) => recordReviewDecision(acc, decision),
    preferences || {}
  )
);

export const loadReviewDecisionStore = (characterId) => {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const raw = storage.getItem(buildReviewDecisionStorageKey(characterId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const saveReviewDecisionStore = (characterId, preferences) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(
      buildReviewDecisionStorageKey(characterId),
      JSON.stringify(pruneReviewDecisions(preferences || {}))
    );
  } catch (error) {
    // Ignore storage quota / availability issues; SmartPaste still works without persistence.
  }
};

const SmartPasteReviewMemory = {
  buildReviewDecisionStorageKey,
  buildReviewDecisionKeys,
  getRememberedReviewCategories,
  recordReviewDecision,
  recordReviewDecisions,
  loadReviewDecisionStore,
  saveReviewDecisionStore
};

export default SmartPasteReviewMemory;
